import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Token storage
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem('auth_token');
  }
  return authToken;
}

export function clearAuthToken() {
  authToken = null;
  localStorage.removeItem('auth_token');
}

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================
// Types
// ==================

export interface User {
  id: string;
  username: string;
  name: string;
  role: string;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface SensorCondition {
  sensor_device_id: string;
  sensor_register: string;
  operator: string;
  threshold: number;
}

export interface BlendRecipeIngredient {
  name: string;
  amount_kg: number;
  cumulative_kg: number;
}

export interface BlendRecipe {
  total_kg: number;
  ingredients: BlendRecipeIngredient[];
}

export interface Blend {
  id: string;
  name: string;
  required_roast_time_seconds: number;
  target_color_rgb?: [number, number, number];
  target_roast_score?: number;
  recipe?: BlendRecipe | null;
  start_condition?: SensorCondition | null;
  end_condition?: SensorCondition | null;
  sort_order?: number | null;
  created_at: string;
  updated_at: string;
}

export interface PackageTemplate {
  id: string;
  customer_name: string;
  blend_id?: string;
  blend_name: string;
  bag_type: string;
  created_at: string;
  updated_at: string;
}

export interface PackagingJob {
  id: string;
  plan_date: string;
  template_id: string;
  customer_name: string;
  blend_id: string;
  blend_name: string;
  bag_type: string;
  all_batch: boolean;
  bag_count?: number | null;
  batch_ids: string[];
  loading_worker_name?: string | null;
  instructions: PackagingInstruction[];
  card_kind: 'batch' | 'instruction' | 'whole_batch' | 'remainder';
  status: 'pending' | 'done';
  sort_order: number;
  completed_at?: string;
  completed_by?: string;
  created_at: string;
  updated_at: string;
}

export interface PackagingInstruction {
  id: string;
  template_id: string;
  customer_name: string;
  bag_type: string;
  all_batch: boolean;
  bag_count?: number | null;
}

export interface PackagingInstructionInput {
  template_id?: string;
  customer_name?: string;
  bag_type?: string;
  all_batch: boolean;
  bag_count?: number;
  save_as_template?: boolean;
}

export interface PackageRequirement {
  id: string;
  template_id: string;
  customer_name: string;
  comment: string;
  all_batch: boolean;
  bag_count?: number | null;
}

export interface RoastBatch {
  id: string;
  blend_id: string;
  blend_name: string;
  blend_target_color_rgb?: [number, number, number];
  blend_target_roast_score?: number;
  blend_recipe?: BlendRecipe | null;
  batch_weight_kg?: number;
  quantity: number;
  unit: string;
  status: 'planned' | 'in-progress' | 'done';
  plan_date: string;
  sort_order: number;
  start_time?: string;
  end_time?: string;
  operator_id?: string;
  loading_worker_name?: string;
  machine_id?: string;
  oven_slot?: 'active' | 'loading';
  planned_roast_time_seconds?: number;
  timer_started_at?: string;
  timer_duration_seconds?: number;
  timer_start_source?: 'sensor';
  remaining_seconds?: number;
  timer_running?: boolean;
  actual_roast_label?: string;
  actual_color_rgb?: [number, number, number];
  color_labeled_at?: string;
  color_label_deadline?: string;
  package_requirements: PackageRequirement[];
  created_at: string;
  updated_at: string;
}

export interface SensorReading {
  device_id: string;
  register_address: string;
  register_name: string;
  register_type: string;
  value: string;
  time: string;
}

export interface SensorReadingsResponse {
  readings: SensorReading[];
  count: number;
  start_time: string;
  end_time: string;
  bucket_seconds: number;
}

export interface SensorBlendInterval {
  batch_id: string;
  blend_id: string;
  blend_name: string;
  machine_id: string;
  start_time: string;
  end_time: string;
  status: string;
}

export interface SensorTimelineImage {
  event_id: string;
  device_id: string;
  captured_at: string;
  has_full_image: boolean;
  has_roi_image: boolean;
  batch_id?: string | null;
  blend_name?: string | null;
}

export interface SensorTimelineOven {
  machine_id: string;
  modbus_device_id?: string | null;
  vision_device_id?: string | null;
  blend_intervals: SensorBlendInterval[];
  images: SensorTimelineImage[];
}

export interface SensorTimelineContext {
  start_time: string;
  end_time: string;
  image_bucket_seconds: number;
  ovens: SensorTimelineOven[];
}

export interface OvenQueue {
  pending_batch_ids: string[];
  revision: number;
}

export type OvenQueues = Record<string, OvenQueue>;

export interface DailyPlan {
  plan_date: string;
  batches: RoastBatch[];
  summary: {
    total: number;
    planned: number;
    in_progress: number;
    done: number;
    total_quantity: number;
  };
  stray_occupants: RoastBatch[];
  oven_queues: OvenQueues;
}

export interface BlendStats {
  blend_id: string;
  blend_name: string;
  total_batches: number;
  done_batches: number;
  planned_batches: number;
  in_progress_batches: number;
  total_quantity: number;
  done_quantity: number;
  avg_roast_duration_seconds: number | null;
  required_roast_time_seconds: number | null;
}

export interface OvenBlendBreakdown {
  blend_name: string;
  count: number;
  quantity: number;
}

export interface OvenStats {
  machine_id: string;
  total_batches: number;
  done_batches: number;
  in_progress_batches: number;
  total_quantity: number;
  done_quantity: number;
  avg_roast_duration_seconds: number | null;
  total_roast_time_seconds: number;
  blend_breakdown: OvenBlendBreakdown[];
}

export interface AnalyticsData {
  start_date: string;
  end_date: string;
  total_batches: number;
  blend_stats: BlendStats[];
  oven_stats: OvenStats[];
}

export interface VisionEvent {
  id: string;
  session_id?: string;
  device_id: string;
  captured_at: string;
  roast_time_seconds?: number;
  calibrated: boolean;
  valid_reading?: boolean;
  average_rgb?: number[];
  average_hsv?: number[];
  average_lab?: number[];
  brownness_score?: number;
  brownness_rate?: number;
  roast_level_score?: number;
  roast_level_label?: string;
  roast_level_histogram?: number[];
  predicted_cooled_rgb?: number[];
  target_cooled_rgb?: number[];
  target_distance?: number;
  stop_recommended: boolean;
  features: Record<string, unknown>;
  full_image_key?: string;
  roi_image_key?: string;
  full_image_url?: string;
  roi_image_url?: string;
}

export type VisionCommandType =
  | 'camera_settings.update'
  | 'calibration.capture'
  | 'calibration.apply'
  | 'calibration.auto'
  | 'calibration.levels'
  | 'calibration.set_labels'
  | 'levels.get'
  | 'image.capture'
  | 'roi.update'
  | 'roi.reset';

export interface VisionCommand {
  id: string;
  device_id: string;
  type: VisionCommandType;
  payload: Record<string, unknown>;
  status: 'queued' | 'dispatched' | 'succeeded' | 'failed';
  created_at: string;
  updated_at: string;
  dispatched_at?: string;
  completed_at?: string;
  created_by?: string;
  result?: Record<string, unknown>;
  error?: string;
}

export interface OvenConfig {
  machine_id: string;
  vision_device_id?: string;
  modbus_device_id?: string;
  automation_rules?: OvenTriggerRule[] | null;
  updated_at?: string;
}

export interface OvenTriggerRule {
  event_type: 'roast_start' | 'roast_end';
  register_name: string;
  operator:
    | 'greater_than'
    | 'greater_than_or_equal'
    | 'less_than'
    | 'less_than_or_equal'
    | 'equal'
    | 'not_equal'
    | 'increase_by'
    | 'decrease_by';
  threshold: number;
  consecutive_samples_required: number;
  enabled: boolean;
}

export interface OvenAutomationRulesConfig {
  machine_id: string;
  rules: OvenTriggerRule[];
}

export interface ServiceLogs {
  services: string[];
  lines: number;
  log: string;
}

// ==================
// Auth API
// ==================

export async function login(username: string, password: string): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/login', { username, password });
  return response.data;
}

export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>('/auth/me');
  return response.data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
  clearAuthToken();
}

// ==================
// Blends API
// ==================

export async function listBlends(): Promise<Blend[]> {
  const response = await apiClient.get<Blend[]>('/blends');
  return response.data;
}

export async function reorderBlends(blendIds: string[]): Promise<Blend[]> {
  const response = await apiClient.post<Blend[]>('/blends/reorder', { blend_ids: blendIds });
  return response.data;
}

export async function createBlend(
  name: string,
  requiredRoastTimeSeconds: number,
  targetColorRgb?: [number, number, number] | null,
  targetRoastScore?: number | null,
  startCondition?: SensorCondition | null,
  endCondition?: SensorCondition | null,
  recipe?: BlendRecipe | null,
): Promise<Blend> {
  const response = await apiClient.post<Blend>('/blends', {
    name,
    required_roast_time_seconds: requiredRoastTimeSeconds,
    target_color_rgb: targetColorRgb ?? null,
    target_roast_score: targetRoastScore ?? null,
    start_condition: startCondition ?? null,
    end_condition: endCondition ?? null,
    recipe: recipe ?? null,
  });
  return response.data;
}

export async function updateBlend(
  blendId: string,
  updates: {
    name?: string;
    required_roast_time_seconds?: number;
    target_color_rgb?: [number, number, number] | null;
    target_roast_score?: number | null;
    start_condition?: SensorCondition | null;
    end_condition?: SensorCondition | null;
    recipe?: BlendRecipe | null;
  },
): Promise<Blend> {
  const response = await apiClient.patch<Blend>(`/blends/${blendId}`, updates);
  return response.data;
}

export async function deleteBlend(blendId: string): Promise<void> {
  await apiClient.delete(`/blends/${blendId}`);
}

// ==================
// Package Templates API
// ==================

export async function listPackageTemplates(): Promise<PackageTemplate[]> {
  const response = await apiClient.get<PackageTemplate[]>('/package-templates');
  return response.data;
}

export async function createPackageTemplate(customerName: string, blendId: string, bagType: string): Promise<PackageTemplate> {
  const response = await apiClient.post<PackageTemplate>('/package-templates', { customer_name: customerName, blend_id: blendId, bag_type: bagType });
  return response.data;
}

export async function updatePackageTemplate(templateId: string, updates: { customer_name?: string; blend_id?: string; bag_type?: string }): Promise<PackageTemplate> {
  const response = await apiClient.patch<PackageTemplate>(`/package-templates/${templateId}`, updates);
  return response.data;
}

export async function deletePackageTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/package-templates/${templateId}`);
}

// ==================
// Batches API
// ==================

export async function getDailyPlan(planDate: string, includeCarryOver = false): Promise<DailyPlan> {
  const response = await apiClient.get<DailyPlan>(`/batches/daily/${planDate}`, {
    params: { include_carry_over: includeCarryOver },
  });
  return response.data;
}

export async function createBatch(
  blendId: string,
  quantity: number,
  unit: string,
  planDate: string,
  batchWeightKg = 90,
  plannedRoastTimeSeconds?: number,
  loadingWorkerName?: string,
  packaging?: PackagingInstructionInput,
): Promise<RoastBatch[]> {
  const response = await apiClient.post<RoastBatch[]>('/batches', {
    blend_id: blendId,
    quantity,
    unit,
    batch_weight_kg: batchWeightKg,
    planned_roast_time_seconds: plannedRoastTimeSeconds,
    plan_date: planDate,
    loading_worker_name: loadingWorkerName,
    packaging,
  });
  return response.data;
}

// ==================
// Packaging Jobs API
// ==================

export async function listPackagingJobs(planDate: string, includeCarryOver = false): Promise<PackagingJob[]> {
  const response = await apiClient.get<PackagingJob[]>('/packaging-jobs', {
    params: { plan_date: planDate, include_carry_over: includeCarryOver },
  });
  return response.data;
}

export async function createPackagingJob(payload: {
  plan_date: string;
  blend_id: string;
  template_id?: string;
  customer_name?: string;
  bag_type?: string;
  bag_count: number;
  batch_ids?: string[];
  save_as_template?: boolean;
}): Promise<PackagingJob> {
  const response = await apiClient.post<PackagingJob>('/packaging-jobs', payload);
  return response.data;
}

export async function updatePackagingJob(jobId: string, updates: { customer_name?: string; blend_id?: string; bag_type?: string; all_batch?: boolean; bag_count?: number }): Promise<PackagingJob> {
  const response = await apiClient.patch<PackagingJob>(`/packaging-jobs/${jobId}`, updates);
  return response.data;
}

export async function deletePackagingJob(jobId: string): Promise<void> {
  await apiClient.delete(`/packaging-jobs/${jobId}`);
}

export async function completePackagingJob(jobId: string): Promise<PackagingJob> {
  const response = await apiClient.post<PackagingJob>(`/packaging-jobs/${jobId}/done`);
  return response.data;
}

export async function getBatch(batchId: string): Promise<RoastBatch> {
  const response = await apiClient.get<RoastBatch>(`/batches/${batchId}`);
  return response.data;
}

export async function reopenPackagingJob(jobId: string): Promise<PackagingJob> {
  const response = await apiClient.post<PackagingJob>(`/packaging-jobs/${jobId}/reopen`);
  return response.data;
}

export async function reorderPackagingJobs(planDate: string, status: 'pending' | 'done', jobIds: string[]): Promise<PackagingJob[]> {
  const response = await apiClient.post<PackagingJob[]>('/packaging-jobs/reorder', { status, job_ids: jobIds }, { params: { plan_date: planDate } });
  return response.data;
}

export async function reorderCarryOverPackagingJobs(planDate: string, jobIds: string[]): Promise<PackagingJob[]> {
  const response = await apiClient.post<PackagingJob[]>(
    '/packaging-jobs/reorder-carry-over',
    { status: 'pending', job_ids: jobIds },
    { params: { plan_date: planDate } },
  );
  return response.data;
}

export async function updateBatch(batchId: string, updates: { quantity?: number; unit?: string; status?: string; sort_order?: number; blend_id?: string; blend_name?: string; planned_roast_time_seconds?: number; loading_worker_name?: string | null }): Promise<RoastBatch> {
  const response = await apiClient.patch<RoastBatch>(`/batches/${batchId}`, updates);
  return response.data;
}

export async function startBatch(batchId: string, machineId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/start`, { machine_id: machineId });
  return response.data;
}

// Assign a planned batch to an oven without starting it (leaves the shared queue).
export async function assignBatch(batchId: string, machineId: string, loadingWorkerName: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/assign`, {
    machine_id: machineId,
    loading_worker_name: loadingWorkerName,
  });
  return response.data;
}

// Remove a planned batch's oven assignment (back to the shared queue).
export async function unassignBatch(batchId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/unassign`);
  return response.data;
}

export async function startTimer(batchId: string, source?: 'sensor'): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(
    `/batches/${batchId}/start-timer`,
    undefined,
    { params: source ? { source } : undefined },
  );
  return response.data;
}

export async function finishBatch(batchId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/finish`);
  return response.data;
}

export async function cancelBatch(batchId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/cancel`);
  return response.data;
}

export async function reorderBatches(batchIds: string[]): Promise<RoastBatch[]> {
  const response = await apiClient.post<RoastBatch[]>('/batches/reorder', { batch_ids: batchIds });
  return response.data;
}

export async function deleteBatch(batchId: string): Promise<void> {
  await apiClient.delete(`/batches/${batchId}`);
}

export async function createPackageRequirement(batchId: string, payload: { template_id?: string; customer_name?: string; comment?: string; all_batch: boolean; bag_count?: number }): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/package-requirements`, payload);
  return response.data;
}

export async function updatePackageRequirement(batchId: string, requirementId: string, updates: { template_id?: string; customer_name?: string; comment?: string; all_batch?: boolean; bag_count?: number }): Promise<RoastBatch> {
  const response = await apiClient.patch<RoastBatch>(`/batches/${batchId}/package-requirements/${requirementId}`, updates);
  return response.data;
}

export async function deletePackageRequirement(batchId: string, requirementId: string): Promise<RoastBatch> {
  const response = await apiClient.delete<RoastBatch>(`/batches/${batchId}/package-requirements/${requirementId}`);
  return response.data;
}

// ==================
// Analytics API
// ==================

export async function getAnalytics(startDate: string, endDate: string): Promise<AnalyticsData> {
  const response = await apiClient.get<AnalyticsData>('/analytics', {
    params: { start_date: startDate, end_date: endDate },
  });
  return response.data;
}

// ==================
// Vision API
// ==================

export async function listVisionEvents(limit = 25): Promise<VisionEvent[]> {
  const response = await apiClient.get<VisionEvent[]>('/vision/events', { params: { limit } });
  return response.data;
}

export async function getLatestVisionEvent(deviceId?: string): Promise<VisionEvent> {
  const response = await apiClient.get<VisionEvent>('/vision/events/latest', {
    params: deviceId ? { device_id: deviceId } : undefined,
  });
  return response.data;
}

export async function listVisionDevices(): Promise<string[]> {
  const response = await apiClient.get<string[]>('/vision/devices');
  return response.data;
}

export async function getVisionEvent(eventId: string): Promise<VisionEvent> {
  const response = await apiClient.get<VisionEvent>(`/vision/events/${eventId}`);
  return response.data;
}

export async function createVisionCommand(
  deviceId: string,
  type: VisionCommandType,
  payload: Record<string, unknown> = {}
): Promise<VisionCommand> {
  const response = await apiClient.post<VisionCommand>(`/vision/devices/${deviceId}/commands`, { type, payload });
  return response.data;
}

export async function listVisionCommands(deviceId: string, limit = 25): Promise<VisionCommand[]> {
  const response = await apiClient.get<VisionCommand[]>(`/vision/devices/${deviceId}/commands`, { params: { limit } });
  return response.data;
}

export interface VisionDeviceStatus {
  device_id: string;
  last_event_at: string | null;
  last_command_interaction_at: string | null;
  pending_commands: number;
  dispatched_commands: number;
  recent_commands: { id: string; type: string; status: string; updated_at: string }[];
}

export async function getVisionDeviceStatus(deviceId: string): Promise<VisionDeviceStatus> {
  const response = await apiClient.get<VisionDeviceStatus>(`/vision/devices/${deviceId}/status`);
  return response.data;
}

// ==================
// Oven Config API
// ==================

export async function getOvenConfig(machineId: string): Promise<OvenConfig> {
  const response = await apiClient.get<OvenConfig>(`/ovens/${machineId}/config`);
  return response.data;
}

export async function setOvenConfig(
  machineId: string,
  visionDeviceId: string | null | undefined,
  modbusDeviceId?: string | null,
): Promise<OvenConfig> {
  const body: Record<string, string | null> = {};
  if (visionDeviceId !== undefined) body.vision_device_id = visionDeviceId;
  if (modbusDeviceId !== undefined) body.modbus_device_id = modbusDeviceId;
  const response = await apiClient.put<OvenConfig>(`/ovens/${machineId}/config`, body);
  return response.data;
}

export async function getModbusDevices(): Promise<{ device_id: string; last_seen: string }[]> {
  const response = await apiClient.get<{ devices: { device_id: string; last_seen: string }[]; count: number }>(
    '/sensors/devices',
  );
  return response.data.devices;
}

export async function getOvenAutomationRules(machineId: string): Promise<OvenAutomationRulesConfig> {
  const response = await apiClient.get<OvenAutomationRulesConfig>(`/ovens/${machineId}/automation-rules`);
  return response.data;
}

export async function setOvenAutomationRules(
  machineId: string,
  rules: OvenTriggerRule[],
): Promise<OvenAutomationRulesConfig> {
  const response = await apiClient.put<OvenAutomationRulesConfig>(`/ovens/${machineId}/automation-rules`, {
    machine_id: machineId,
    rules,
  });
  return response.data;
}

// ==================
// Logs API
// ==================

export async function getServiceLogs(services: string[], lines = 200): Promise<ServiceLogs> {
  const response = await apiClient.get<ServiceLogs>('/logs', {
    params: { services, lines },
    paramsSerializer: { indexes: null },
  });
  return response.data;
}

// ==================
// Sensor API
// ==================

export interface SensorReadingsQuery {
  startTime: string;
  endTime: string;
  device?: string;
  register?: string;
}

export async function getSensorReadings(query: SensorReadingsQuery): Promise<SensorReadingsResponse>;
export async function getSensorReadings(hours?: number, device?: string, register?: string): Promise<SensorReading[]>;
export async function getSensorReadings(
  queryOrHours: SensorReadingsQuery | number = 1,
  device?: string,
  register?: string,
): Promise<SensorReadingsResponse | SensorReading[]> {
  if (typeof queryOrHours === 'number') {
    const response = await apiClient.get<SensorReadingsResponse>('/sensors/readings', {
      params: { hours: queryOrHours, device, register },
    });
    return response.data.readings;
  }
  const response = await apiClient.get<SensorReadingsResponse>('/sensors/readings', {
    params: {
      start_time: queryOrHours.startTime,
      end_time: queryOrHours.endTime,
      device: queryOrHours.device,
      register: queryOrHours.register,
    },
  });
  return response.data;
}

export async function getSensorTimelineContext(
  startTime: string,
  endTime: string,
  device?: string,
): Promise<SensorTimelineContext> {
  const response = await apiClient.get<SensorTimelineContext>('/sensors/timeline-context', {
    params: { start_time: startTime, end_time: endTime, device },
  });
  return response.data;
}

export function getVisionEventImagePath(eventId: string, kind: 'full' | 'roi'): string {
  return `/vision/events/${encodeURIComponent(eventId)}/images/${kind}`;
}

export function getVisionEventImageUrl(eventId: string, kind: 'full' | 'roi'): string {
  return `${API_BASE_URL.replace(/\/$/, '')}${getVisionEventImagePath(eventId, kind)}`;
}

export async function getVisionEventImageObjectUrl(
  eventId: string,
  kind: 'full' | 'roi',
): Promise<string> {
  const response = await apiClient.get<Blob>(getVisionEventImagePath(eventId, kind), {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
}

export async function getLatestSensorReadings(device?: string): Promise<SensorReading[]> {
  const response = await apiClient.get<{ latest: SensorReading[]; count: number }>('/sensors/latest', {
    params: device ? { device } : undefined,
  });
  return response.data.latest;
}

export async function labelBatchColor(
  batchId: string,
  roastLabel: string,
  actualColorRgb: [number, number, number],
): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/color-label`, {
    roast_label: roastLabel,
    actual_color_rgb: actualColorRgb,
  });
  return response.data;
}

export default apiClient;
